import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowDownIcon, BriefcaseIcon, UserRoundIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { InquiryForm } from '../components/InquiryForm';
import type { InquiryType } from '../types/content';
const VALID_TYPES: InquiryType[] = ['employer', 'candidate', 'general', 'media'];
const pathways = [{
  type: 'employer' as InquiryType,
  label: 'For Employers',
  title: 'Discuss Your Hiring Needs',
  description: 'Board, CEO, C-suite, or senior leadership search, assessment, and succession advisory.',
  icon: BriefcaseIcon
}, {
  type: 'candidate' as InquiryType,
  label: 'For Candidates',
  title: 'Introduce Yourself',
  description: 'Share your background in confidence so we can consider you for current and future mandates.',
  icon: UserRoundIcon
}];
export function Contact() {
  const [params] = useSearchParams();
  const rawType = params.get('type') as InquiryType | null;
  const selectedType: InquiryType | '' = rawType && VALID_TYPES.includes(rawType) ? rawType : '';
  const role = params.get('role');
  const initialMessage = role ? `I'd like to express interest in the ${role} opportunity.` : '';
  return <>
      <PageHero breadcrumb="Contact Us" title="Begin a confidential conversation." lead="Choose the pathway that fits, or send a general inquiry. Every message is read by our team and handled in strict confidence." data-id="element-225" data-mp="1j6typ0ptfytj" />

      <section className="bg-ivory" data-id="element-226" data-mp="qixtf7m6kyl5">
        <div className="mx-auto max-w-site px-6 pt-16 lg:px-10 lg:pt-20" data-id="element-227" data-mp="qjzhccoapfka">
          <ul className="grid gap-6 md:grid-cols-2" data-id="element-228" data-mp="vfoa9x5t7y7m">
            {pathways.map(p => {
            const active = selectedType === p.type;
            const Icon = p.icon;
            return <li key={p.type} data-id="element-229" data-mp="26w66px9eq0u9">
                  <Link to={`/contact?type=${p.type}#inquiry-form`} replace aria-current={active ? 'true' : undefined} className={`group flex h-full flex-col rounded-3xl p-8 transition-colors duration-150 ease-out lg:p-10 ${active ? 'bg-midnight-900 text-champagne-50' : 'bg-white text-midnight-900 ring-1 ring-midnight-900/10 hover:ring-midnight-900/30'}`} data-id="element-230" data-mp="2bviuqlc0ldyf">
                    <div className="flex items-center gap-3" data-id="element-231" data-mp="13gm68ndsw96s">
                      <Icon className={`h-5 w-5 ${active ? 'text-champagne-300' : 'text-champagne-600'}`} aria-hidden="true" data-id="element-232" data-mp="13h5cv7y03b5i" />
                      <span className={`text-sm font-medium ${active ? 'text-champagne-300' : 'text-champagne-600'}`} data-id="element-233" data-mp="1d6oyumh0fl4l">{p.label}</span>
                    </div>
                    <h2 className="mt-5 font-serif text-3xl md:text-4xl" data-id="element-234" data-mp="18edzbkq6m1ck">{p.title}</h2>
                    <p className={`mt-3 text-[15px] leading-relaxed ${active ? 'text-champagne-100/70' : 'text-midnight-900/70'}`} data-id="element-235" data-mp="kp34ejvij16c">{p.description}</p>
                    <span className={`mt-auto inline-flex items-center gap-2 pt-8 text-sm font-medium ${active ? 'text-champagne-200' : 'text-midnight-900'}`} data-id="element-236" data-mp="2remxw0pqnyrf">
                      {active ? 'Selected below' : 'Start here'}
                      <ArrowDownIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-y-0.5" aria-hidden="true" data-id="element-237" data-mp="2hh8hu27q9bzn" />
                    </span>
                  </Link>
                </li>;
          })}
          </ul>
        </div>

        <div id="inquiry-form" className="mx-auto grid max-w-site scroll-mt-36 gap-12 px-6 py-16 lg:grid-cols-12 lg:px-10 lg:py-24" data-id="element-238" data-mp="3f47foga5opz4">
          <div className="lg:col-span-4" data-id="element-239" data-mp="313elxxrohyx2">
            <h2 className="font-serif text-4xl text-midnight-900" data-id="element-240" data-mp="316iwdcvqlqw6">Send an inquiry</h2>
            <p className="mt-4 text-[16px] leading-relaxed text-midnight-900/70" data-id="element-241" data-mp="3548a8s919fqh">
              Tell us a little about yourself and what you’d like to discuss. We typically respond within two business days.
            </p>
            <dl className="mt-10 space-y-6 border-t border-midnight-900/15 pt-8 text-[15px]" data-id="element-242" data-mp="1tojog47immr9">
              <div data-id="element-243" data-mp="29zu5dtkhec1d">
                <dt className="font-medium text-midnight-900" data-id="element-244" data-mp="257kgmgv3ri0o">Confidentiality</dt>
                <dd className="mt-1 leading-relaxed text-midnight-900/70" data-id="element-245" data-mp="255hljif3eyp3">Nothing you share is passed to a client or third party without your consent.</dd>
              </div>
              <div data-id="element-246" data-mp="1vaxza21zj93e">
                <dt className="font-medium text-midnight-900" data-id="element-247" data-mp="1v7a4z0opuinm">What happens next</dt>
                <dd className="mt-1 leading-relaxed text-midnight-900/70" data-id="element-248" data-mp="2susnh386iz4t">A senior member of our team reviews your inquiry and replies personally.</dd>
              </div>
            </dl>
          </div>
          <div className="lg:col-span-8" data-id="element-249" data-mp="2j0sbchehctml">
            <InquiryForm initialType={selectedType} initialMessage={initialMessage} data-id="element-250" data-mp="2exn9mh284vz0" />
          </div>
        </div>
      </section>
    </>;
}